import { useId, useRef } from 'react';
import { useI18n } from '../i18n';
import { ModalShell } from './ModalShell';

/**
 * MODALE DE CONFIRMATION réutilisable pour les actions DESTRUCTIVES de l'explorateur
 * (effacement récursif d'un dossier, effacement groupé de fichiers). Message explicite
 * sur le caractère irréversible ; le bouton de confirmation est teinté `danger` (jamais
 * l'information par la couleur seule : il porte aussi son libellé). Le focus initial va sur
 * ANNULER (choix non destructif par défaut). Construite sur `ModalShell` (piège de focus,
 * Échap, clic sur le fond, restauration du focus).
 *
 * `details` est un ENCART optionnel rendu sous le message — l'impact CHIFFRÉ d'un effacement
 * (XFILE-33). Optionnel par construction : l'appelant qui n'a rien à chiffrer (ou dont le calcul
 * a échoué) ne le passe pas, et la modale retombe sur le seul message générique.
 */
export function ConfirmDialog({
  title,
  message,
  details,
  confirmLabel,
  onConfirm,
  onCancel,
  busy = false,
}: {
  title: string;
  message: string;
  /** Encart d'impact facultatif, rendu entre le message et les boutons. */
  details?: React.ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
}): React.JSX.Element {
  const { t } = useI18n();
  const titleId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);

  return (
    <ModalShell labelledBy={titleId} onClose={onCancel} initialFocusRef={cancelRef}>
      <div className="flex flex-col gap-4 p-5">
        <h2 id={titleId} className="text-base font-semibold">
          {title}
        </h2>
        <p className="text-sm text-muted">{message}</p>
        {details}
        <div className="flex flex-wrap justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className="rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            {t('storage.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="rounded-md bg-danger px-3 py-1.5 text-sm font-medium text-danger-fg disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
